import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createToggleRow, safeStorage } from '@learnforge/demo-kit';
import {
  type Expr,
  type RuleName,
  parse,
  format,
  canonicalise,
  applicableSites,
  applyRule,
  autoSimplify,
  equivalent,
} from '../algebra';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createSvgEl } from '@learnforge/demo-render';
import { mountActionBar } from '../action-bar';
import { REWRITER_PRESETS } from '../data/rewriter-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Get the list of rule names that apply at some node in the expression.
 */
export function getApplicableRules(expr: Expr): RuleName[] {
  const allRules: RuleName[] = [
    'distribute',
    'collect-like-terms',
    'factor-common-monomial',
    'cancel-common-factor',
    'simplify-fraction',
    'flatten-add',
    'flatten-mul',
    'exponent-product',
    'exponent-power',
    'exponent-quotient',
    'negative-exponent',
    'zero-exponent',
    'log-product',
    'log-power',
    'log-quotient',
    'change-of-base',
    'radical-to-frac-exp',
    'frac-exp-to-radical',
    'rationalize-denominator',
    'multiply-by-conjugate',
  ];

  const applicable: RuleName[] = [];
  for (const rule of allRules) {
    if (applicableSites(expr, rule).length > 0) {
      applicable.push(rule);
    }
  }
  return applicable;
}

/**
 * Get the paths where a specific rule applies in an expression.
 */
export function highlightApplicableNodes(
  expr: Expr,
  rule: RuleName,
): readonly (readonly number[])[] {
  return applicableSites(expr, rule);
}

/**
 * Check if the current expression has reached the target form.
 */
export function reachedTarget(current: Expr, target: Expr): boolean {
  return equivalent(current, target);
}

// ============================================================================
// History Recording
// ============================================================================

interface HistoryEntry {
  expr: Expr;
  rule: RuleName | null;
}

function formatExprForDisplay(expr: Expr): string {
  return format(expr);
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  advancedMode: boolean;
  currentExpr: Expr;
  targetExpr: Expr;
  history: HistoryEntry[];
  isAutoMode: boolean;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.expression-rewriter.label',
    demoId: 'algebra',
    advancedKey: 'expression-rewriter',

    initialState(ctx): State {
      const defaultPreset = REWRITER_PRESETS[0]!;
      const currentExpr = parse(defaultPreset.start);
      const targetExpr = defaultPreset.target
        ? parse(defaultPreset.target)
        : canonicalise(currentExpr);
      return {
        presetId: defaultPreset.id,
        advancedMode: ctx.advanced,
        currentExpr,
        targetExpr,
        history: [{ expr: currentExpr, rule: null }],
        isAutoMode: !ctx.advanced,
      };
    },

    applyPreset(state, presetId): State {
      const preset = REWRITER_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      const currentExpr = parse(preset.start);
      const targetExpr = preset.target ? parse(preset.target) : canonicalise(currentExpr);
      return {
        ...state,
        presetId: preset.id,
        currentExpr,
        targetExpr,
        history: [{ expr: currentExpr, rule: null }],
      };
    },

    applyShotState(state): State {
      return { ...state, isAutoMode: false };
    },

    createRender(ctx, host, controls) {
      // ── SVG Stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'alg-expr-rewriter-svg' });
      host.appendChild(svg);

      const leftText = createSvgEl('text', {
        x: '50',
        y: '100',
        'text-anchor': 'start',
        class: 'alg-text',
      });
      svg.appendChild(leftText);

      const arrowGroup = createSvgEl('g');
      const arrowLine = createSvgEl('line', {
        x1: '350',
        y1: '100',
        x2: '450',
        y2: '100',
        class: 'alg-line-strong',
        'marker-end': 'url(#arrowhead)',
      });
      arrowGroup.appendChild(arrowLine);

      const arrowLabel = createSvgEl('text', {
        x: '400',
        y: '85',
        'text-anchor': 'middle',
        class: 'alg-text-soft',
      });
      arrowLabel.textContent = '';
      arrowGroup.appendChild(arrowLabel);
      svg.appendChild(arrowGroup);

      const rightText = createSvgEl('text', {
        x: '500',
        y: '100',
        'text-anchor': 'start',
        class: 'alg-text',
      });
      svg.appendChild(rightText);

      // Arrow marker definition
      const defs = createSvgEl('defs');
      const marker = createSvgEl('marker', {
        id: 'arrowhead',
        markerWidth: '10',
        markerHeight: '10',
        refX: '9',
        refY: '3',
        orient: 'auto',
      });
      const polygon = createSvgEl('polygon', {
        points: '0 0, 10 3, 0 6',
        class: 'alg-line-strong',
      });
      marker.appendChild(polygon);
      defs.appendChild(marker);
      svg.appendChild(defs);

      // ── Panel: Expression input ──
      const exprInputRow = document.createElement('div');
      exprInputRow.className = 'alg-panel-row';
      const exprLabel = document.createElement('label');
      exprLabel.textContent = ctx.i18n.t('panel.enter-number');
      const exprInput = document.createElement('input');
      exprInput.type = 'text';
      exprInput.className = 'alg-text-input';
      exprInput.addEventListener('change', () => {
        controls.setState((s) => {
          try {
            const parsed = canonicalise(parse(exprInput.value));
            return {
              ...s,
              currentExpr: parsed,
              history: [{ expr: parsed, rule: null }],
            };
          } catch (_e) {
            // restore on bad input — guard happens in update()
            return s;
          }
        });
      });
      exprInputRow.appendChild(exprLabel);
      exprInputRow.appendChild(exprInput);
      ctx.panel.appendChild(exprInputRow);

      // ── Panel: Target expression input (advanced only) — stable, hidden/shown ──
      const targetRow = document.createElement('div');
      targetRow.className = 'alg-panel-row alg-target-row';
      targetRow.style.display = 'none';
      const targetLabel = document.createElement('label');
      targetLabel.textContent = 'Target:';
      const targetInput = document.createElement('input');
      targetInput.type = 'text';
      targetInput.className = 'alg-text-input';
      targetInput.addEventListener('change', () => {
        controls.setState((s) => {
          try {
            const parsed = canonicalise(parse(targetInput.value));
            return { ...s, targetExpr: parsed };
          } catch (_e) {
            return s;
          }
        });
      });
      targetRow.appendChild(targetLabel);
      targetRow.appendChild(targetInput);
      ctx.panel.appendChild(targetRow);

      // ── Panel: Auto/interactive mode toggle ──
      const modeToggle = createToggleRow({
        labelKey: 'btn.auto-arrange',
        i18n: ctx.i18n,
        checked: true, // reconciled in first update()
        onChange: (v) => {
          controls.setState((s) => ({ ...s, isAutoMode: v }));
        },
      });
      ctx.panel.appendChild(modeToggle.element);

      // ── Panel: Advanced toggle ──
      const ADVANCED_KEY = 'algebra.expression-rewriter.advanced';
      const advancedToggle = createToggleRow({
        labelKey: 'btn.advanced',
        i18n: ctx.i18n,
        checked: ctx.advanced,
        onChange: (v) => {
          safeStorage.set(ADVANCED_KEY, v ? 'true' : 'false');
          controls.setState((s) => {
            if (v) {
              // Switching to advanced: interactive mode + first advanced preset
              const firstAdvanced = REWRITER_PRESETS.find((p) => p.advancedOnly);
              if (firstAdvanced) {
                const currentExpr = canonicalise(parse(firstAdvanced.start));
                const targetExpr = firstAdvanced.target
                  ? parse(firstAdvanced.target)
                  : canonicalise(currentExpr);
                return {
                  ...s,
                  advancedMode: true,
                  isAutoMode: false,
                  presetId: firstAdvanced.id,
                  currentExpr,
                  targetExpr,
                  history: [{ expr: currentExpr, rule: null }],
                };
              }
              return { ...s, advancedMode: true, isAutoMode: false };
            } else {
              // Reverting to foundation: auto mode + first foundation preset
              const firstFoundation = REWRITER_PRESETS.find((p) => !p.advancedOnly);
              if (firstFoundation) {
                const currentExpr = canonicalise(parse(firstFoundation.start));
                const targetExpr = firstFoundation.target
                  ? parse(firstFoundation.target)
                  : canonicalise(currentExpr);
                return {
                  ...s,
                  advancedMode: false,
                  isAutoMode: true,
                  presetId: firstFoundation.id,
                  currentExpr,
                  targetExpr,
                  history: [{ expr: currentExpr, rule: null }],
                };
              }
              return { ...s, advancedMode: false, isAutoMode: true };
            }
          });
        },
      });
      ctx.panel.appendChild(advancedToggle.element);

      // ── Panel: Rule palette (advanced + interactive mode only) ──
      const rulePaletteContainer = document.createElement('div');
      rulePaletteContainer.className = 'alg-rule-palette';
      rulePaletteContainer.style.display = 'none';
      ctx.panel.appendChild(rulePaletteContainer);

      // ── Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── Timer state ──
      let autoTimerId: ReturnType<typeof setInterval> | null = null;
      let lastAutoMode: boolean | undefined;
      let lastAdvancedMode: boolean | undefined;

      controls.registerCleanup(() => {
        if (autoTimerId !== null) {
          clearInterval(autoTimerId);
          autoTimerId = null;
        }
      });

      // ── Update ──
      const update = (state: State): void => {
        const { currentExpr, targetExpr, history, isAutoMode, advancedMode } = state;

        // --- Display (leftText / rightText / arrowLabel) ---
        const latestEntry = history[history.length - 1]!;
        const displayExpr = latestEntry.expr;
        leftText.textContent = formatExprForDisplay(displayExpr);
        rightText.textContent = formatExprForDisplay(displayExpr);

        if (latestEntry.rule) {
          arrowLabel.textContent = ctx.i18n.t(`rule.${latestEntry.rule}`);
        } else {
          arrowLabel.textContent = '';
        }

        const targetReached = reachedTarget(currentExpr, targetExpr);
        if (targetReached && !isAutoMode) {
          arrowLabel.textContent = ctx.i18n.t('message.target-form-reached');
        }

        // --- Sync expr input (GUIDANCE B: guard caret) ---
        const exprStr = formatExprForDisplay(currentExpr);
        if (exprInput.value !== exprStr) {
          exprInput.value = exprStr;
        }

        // --- Sync mode toggle checkbox ---
        const modeCheckbox = modeToggle.element.querySelector('input');
        if (modeCheckbox && modeCheckbox.checked !== isAutoMode) {
          modeCheckbox.checked = isAutoMode;
        }

        // --- Show/hide target row (GUIDANCE: stable ref, no DOM insert/remove) ---
        targetRow.style.display = advancedMode ? '' : 'none';
        if (advancedMode) {
          const targetStr = formatExprForDisplay(targetExpr);
          // GUIDANCE B: guard caret
          if (targetInput.value !== targetStr) {
            targetInput.value = targetStr;
          }
        }

        // --- Rule palette ---
        rulePaletteContainer.replaceChildren();
        if (advancedMode && !isAutoMode) {
          rulePaletteContainer.style.display = 'block';
          const applicableRules = getApplicableRules(currentExpr);
          for (const rule of applicableRules) {
            const btn = document.createElement('button');
            btn.className = 'alg-rule-btn';
            btn.textContent = ctx.i18n.t(`rule.${rule}`);
            btn.addEventListener('click', () => {
              controls.setState((s) => {
                const sites = applicableSites(s.currentExpr, rule);
                if (sites.length > 0) {
                  const path = sites[0]!;
                  const result = applyRule(s.currentExpr, rule, path);
                  if (result !== null) {
                    const next = canonicalise(result);
                    return {
                      ...s,
                      currentExpr: next,
                      history: [...s.history, { expr: next, rule }],
                    };
                  }
                }
                return s;
              });
            });
            rulePaletteContainer.appendChild(btn);
          }
        } else {
          rulePaletteContainer.style.display = 'none';
        }

        // --- Timer: start/stop on isAutoMode transitions (GUIDANCE A timer) ---
        if (isAutoMode !== lastAutoMode) {
          if (isAutoMode && autoTimerId === null) {
            autoTimerId = setInterval(() => {
              controls.setState((s) => {
                if (!s.isAutoMode) return s;
                const { steps } = autoSimplify(s.currentExpr);
                if (steps.length > 0) {
                  const step = steps[0]!;
                  const next = step.after;
                  const nextHistory = [...s.history, { expr: next, rule: step.rule }];
                  const nextState = { ...s, currentExpr: next, history: nextHistory };
                  // Check if auto-simplification is exhausted
                  const furtherSteps = autoSimplify(next);
                  if (furtherSteps.steps.length === 0) {
                    // Stop the timer after this tick
                    if (autoTimerId !== null) {
                      clearInterval(autoTimerId);
                      autoTimerId = null;
                    }
                    return { ...nextState, isAutoMode: false };
                  }
                  return nextState;
                } else {
                  if (autoTimerId !== null) {
                    clearInterval(autoTimerId);
                    autoTimerId = null;
                  }
                  return { ...s, isAutoMode: false };
                }
              });
            }, 800);
          } else if (!isAutoMode && autoTimerId !== null) {
            clearInterval(autoTimerId);
            autoTimerId = null;
          }
          lastAutoMode = isAutoMode;
        }

        // --- Action bar (GUIDANCE A: guard on advancedMode change only) ---
        if (advancedMode !== lastAdvancedMode) {
          lastAdvancedMode = advancedMode;

          const availablePresets = advancedMode
            ? REWRITER_PRESETS
            : REWRITER_PRESETS.filter((p) => !p.advancedOnly);

          const baseActions = [
            {
              id: 'reset',
              labelKey: 'btn.reset' as AlgebraKey,
              onClick: () => {
                controls.setState((s) => {
                  const preset = REWRITER_PRESETS.find((p) => p.id === s.presetId)!;
                  const nextExpr = canonicalise(parse(preset.start));
                  const nextTarget = preset.target ? parse(preset.target) : canonicalise(nextExpr);
                  return {
                    ...s,
                    currentExpr: nextExpr,
                    targetExpr: nextTarget,
                    history: [{ expr: nextExpr, rule: null }],
                  };
                });
              },
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise' as AlgebraKey,
              onClick: () => {
                controls.setState((s) => {
                  const idx = Math.floor(ctx.prng() * availablePresets.length);
                  const preset = availablePresets[idx]!;
                  const nextExpr = canonicalise(parse(preset.start));
                  const nextTarget = preset.target ? parse(preset.target) : canonicalise(nextExpr);
                  return {
                    ...s,
                    presetId: preset.id,
                    currentExpr: nextExpr,
                    targetExpr: nextTarget,
                    history: [{ expr: nextExpr, rule: null }],
                  };
                });
              },
            },
          ];

          const actions = advancedMode
            ? [
                ...baseActions,
                {
                  id: 'check-equivalent',
                  labelKey: 'btn.check-equivalent' as AlgebraKey,
                  onClick: () => {
                    // check-equivalent is a transient display action; read live state via setState
                    controls.setState((s) => {
                      if (reachedTarget(s.currentExpr, s.targetExpr)) {
                        arrowLabel.textContent = ctx.i18n.t('message.target-form-reached');
                      } else {
                        arrowLabel.textContent = 'Not equivalent yet';
                      }
                      return s;
                    });
                  },
                },
              ]
            : baseActions;

          actionBar.setActions(actions);
        }
      };

      return {
        update,
        dispose() {
          if (autoTimerId !== null) {
            clearInterval(autoTimerId);
            autoTimerId = null;
          }
          actionBar.clear();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
